import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { setBrowserPrinter, type PrintJob } from "../lib/print.js";
import { useSession } from "../session.js";
import { logoImage } from "../lib/raster.js";

/** QR code image for a payload (UPI link, ticket code). */
export function QrImage({ value, alt, size = 220, className }: { value: string; alt: string; size?: number; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(value, { margin: 1, width: size, errorCorrectionLevel: "M" })
      .then((url) => { if (alive) setSrc(url); })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; };
  }, [value, size]);
  return src ? <img src={src} alt={alt} width={size} height={size} className={className} /> : <span className="tos-muted">Preparing QR…</span>;
}

type Rendered = PrintJob & { qrUrl: string | null; logoUrl: string | null };

/**
 * Browser-print fallback when the device agent is not running: renders the same print jobs as 80 mm (or 58 mm)
 * slips and opens the print dialog. Configure the thermal printer once in Chrome (margins none, no headers).
 */
export function PrintArea() {
  const { paperWidth } = useSession();
  const [jobs, setJobs] = useState<Rendered[]>([]);
  useEffect(() => {
    setBrowserPrinter(async (list) => {
      const logo = (import.meta.env.VITE_RECEIPT_LOGO_URL as string | undefined) ?? "";
      const logoUrl = logo ? (await logoImage(logo)).src : null;
      const rendered = await Promise.all(list.map(async (j) => ({ ...j, logoUrl, qrUrl: j.qr ? await QRCode.toDataURL(j.qr, { margin: 1, width: 240 }) : null })));
      flushSync(() => setJobs(rendered));
      await Promise.all([...document.querySelectorAll<HTMLImageElement>(".pos-print-area img")].map((img) => img.decode().catch(() => undefined)));
      document.body.classList.add("pos-printing");
      try {
        window.print();
      } finally {
        document.body.classList.remove("pos-printing");
      }
    });
    return () => setBrowserPrinter(null);
  }, []);
  return createPortal(
    <div className={`pos-print-area${paperWidth === 32 ? " w58" : ""}`} aria-hidden="true">
      {jobs.map((j, i) => (
        <div className="pos-slip" key={i}>
          {j.logoUrl && <img src={j.logoUrl} alt="Temple logo" style={{ width: 160, maxHeight: 256, objectFit: "contain" }} />}
          {j.lines.map((l, k) => (
            <p key={k} className={[l.align === "center" ? "c" : l.align === "right" ? "r" : "", l.bold ? "b" : "", l.size === 2 ? "s2" : ""].join(" ")}>{l.text || " "}</p>
          ))}
          {j.qrUrl && <img src={j.qrUrl} alt="" />}
        </div>
      ))}
    </div>,
    document.body,
  );
}
